import React from 'react'
import { AlertProps, AlertTitle, Alert as MuiAlert, Box, SxProps, Typography, useTheme } from '@mui/material'
import { NotificationsNone } from '@mui/icons-material'

interface Props {
  variant?: AlertProps['variant']
  severity: AlertProps['severity']
  text: string | React.ReactNode
  onClose?: (event: React.SyntheticEvent) => void
  hide?: boolean
  title?: string
  sx?: SxProps
  customIcon?: React.ReactNode
  actionButton?: React.ReactNode
}

const Alert: React.FC<Props> = ({ variant = 'filled', severity, text, onClose, hide = false, title, sx, customIcon, actionButton }) => {
  const theme = useTheme()
  const severityIsError = severity === 'error'
  const mainColor = severityIsError ? theme.palette.error.main : theme.palette.warning.main
  const lightColor = severityIsError ? theme.palette.error.light : theme.palette.warning.light

  const icon = customIcon ?? <NotificationsNone sx={{ color: mainColor, fontSize: 30 }} />

  if (hide) return <></>

  return (
    <MuiAlert
      sx={{
        backgroundColor: lightColor,
        border: `1px solid ${mainColor}`,
        px: theme.spacing(4),
        py: theme.spacing(2),
        '& .MuiAlert-icon, .MuiAlert-action': {
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiAlert-action': {
          color: theme.palette.grey.grey01,
        },
        '& .MuiAlert-message': {
          flex: '1 1 0%',
          width: '100%',
        },
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
        },
        ...sx,
      }}
      variant={variant}
      severity={severity}
      icon={icon}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexDirection: { xs: actionButton ? 'column' : 'row', sm: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && <AlertTitle sx={{ [theme.breakpoints.down('sm')]: { textAlign: 'center' } }}>{title}</AlertTitle>}
          {typeof text === 'string' ? (
            <Typography variant="body2" sx={{ color: theme.palette.grey.grey01, [theme.breakpoints.down('sm')]: { textAlign: 'center' } }}>
              {text}
            </Typography>
          ) : (
            text
          )}
        </Box>
        {actionButton}
      </Box>
    </MuiAlert>
  )
}

export default Alert
